import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { toast } from 'sonner';
import { Truck, CheckCircle2, AlertTriangle, Loader2, ExternalLink, RefreshCw } from 'lucide-react';

/**
 * SUPER_ADMIN-only "Test Orders" page.
 *
 * Workflow: pick service type (B2B/B2C) → pick courier → (optional) merchant → click
 * "Create & Ship". Backend auto-generates a realistic test payload (customer in Delhi,
 * 5kg parcel for B2B, 1kg for B2C, ₹1000 invoice), creates the Order, and immediately
 * attempts Ship via the real courier API. Result panel surfaces ship status, LR/AWB,
 * pickupId/bookingId, last booking error, and the most recent CourierApiLog rows so
 * ops can verify each courier without filling the full booking form.
 */
export default function TestOrdersPage() {
  const [orderType, setOrderType] = useState<'B2B' | 'B2C'>('B2B');
  const [courierId, setCourierId] = useState<number | ''>('');
  const [merchantId, setMerchantId] = useState<number | ''>('');
  const [skipShip, setSkipShip] = useState(false);
  const [lastResult, setLastResult] = useState<any | null>(null);

  const { data: opts, isLoading: optsLoading, refetch } = useQuery({
    queryKey: ['admin', 'test-orders', 'options'],
    queryFn: async () => (await api.get('/admin/test-orders/options')).data as { couriers: any[]; merchants: any[] },
  });

  // Filter couriers by orderType (the courier's B2B-or-B2C designation).
  // Courier.orderType is the right column; serviceType is the Surface/Air/etc enum.
  // Accept missing/BOTH so older rows still show.
  const filtered = (opts?.couriers ?? []).filter((c: any) => {
    const ot = String(c.orderType ?? '').toUpperCase();
    if (!ot || ot === 'BOTH') return true;
    return ot === orderType;
  });

  const run = useMutation({
    mutationFn: async () => {
      if (!courierId) throw new Error('Pick a courier first');
      const body: any = { orderType, courierId: Number(courierId), skipShip };
      if (merchantId) body.merchantId = Number(merchantId);
      return (await api.post('/admin/test-orders/create-and-ship', body)).data;
    },
    onSuccess: (data) => {
      setLastResult(data);
      if (data.ok) {
        toast.success(`Order ${data.orderNo} shipped — ${data.order?.lrNo || data.order?.awbNo}`);
      } else {
        toast.error(`Order ${data.orderNo} created but ${data.step === 'ship-failed' ? 'ship FAILED' : 'no waybill'}`);
      }
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Test failed';
      toast.error(msg);
      setLastResult({ ok: false, errorMessage: msg });
    },
  });

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center gap-2 mb-1">
        <Truck className="text-emerald-700" size={22} />
        <h1 className="text-xl font-bold">Test Orders</h1>
        <span className="text-[10px] uppercase font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded">SUPER_ADMIN</span>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        One-click create + ship a realistic test order against any courier (real API call). Default payload: customer in Delhi 110001, ₹1000 invoice, 5kg for B2B, 1kg for B2C, 1 box. Use this to verify every courier is reachable without filling the long booking form.
      </p>

      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Service Type</label>
            <div className="flex gap-2">
              {(['B2C', 'B2B'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => { setOrderType(t); setCourierId(''); }}
                  className={`px-4 py-1.5 text-sm rounded border ${orderType === t ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-300'}`}
                >{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Merchant (optional — defaults to first active)</label>
            <select
              value={merchantId}
              onChange={e => setMerchantId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            >
              <option value="">— first active merchant —</option>
              {(opts?.merchants ?? []).map((m: any) => (
                <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Courier ({filtered.length} active for {orderType})
            <button onClick={() => refetch()} className="ml-2 text-emerald-700 hover:underline inline-flex items-center gap-1"><RefreshCw size={10} /> reload</button>
          </label>
          <select
            value={courierId}
            onChange={e => setCourierId(e.target.value ? Number(e.target.value) : '')}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            disabled={optsLoading}
          >
            <option value="">— pick a courier —</option>
            {filtered.map((c: any) => (
              <option key={c.id} value={c.id}>
                #{c.id} · {c.name} ({c.code}) · {c.brand ?? '—'} · {c.orderType ?? c.serviceType ?? '—'}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <input
            id="skipShip"
            type="checkbox"
            checked={skipShip}
            onChange={e => setSkipShip(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="skipShip" className="text-xs text-slate-600">Skip Ship — only create the order (don't call courier API)</label>
        </div>

        <button
          onClick={() => run.mutate()}
          disabled={!courierId || run.isPending}
          className="bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 text-sm rounded font-medium disabled:opacity-50 flex items-center gap-2"
        >
          {run.isPending ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />}
          {run.isPending ? 'Creating & shipping…' : `Create${skipShip ? '' : ' & Ship'} Test ${orderType} Order`}
        </button>
      </div>

      {lastResult && (
        <div className={`mt-5 bg-white border rounded-lg p-5 shadow-sm ${lastResult.ok ? 'border-emerald-200' : 'border-rose-200'}`}>
          <div className="flex items-center gap-2 mb-3">
            {lastResult.ok ? <CheckCircle2 size={18} className="text-emerald-600" /> : <AlertTriangle size={18} className="text-rose-600" />}
            <h2 className="text-sm font-bold">{lastResult.ok ? 'Success' : 'Failed'}</h2>
            <span className="text-xs text-slate-500">{lastResult.step ?? '?'}</span>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {lastResult.orderId && (
              <>
                <div className="text-slate-500">Order</div>
                <div className="font-mono">
                  <Link to={`/orders-${(orderType).toLowerCase()}/${lastResult.orderId}`} className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                    {lastResult.orderNo}
                    <ExternalLink size={10} />
                  </Link>
                </div>
                <div className="text-slate-500">Courier</div>
                <div>#{lastResult.courierId} · {lastResult.courierCode} · {lastResult.courierName}</div>
                <div className="text-slate-500">Merchant</div>
                <div>#{lastResult.merchantId}</div>
                <div className="text-slate-500">Warehouse</div>
                <div>#{lastResult.warehouseId}</div>
              </>
            )}
            {lastResult.order && (
              <>
                <div className="text-slate-500">Status</div>
                <div className="font-semibold">{lastResult.order.statusId === 1 ? 'Shipped' : lastResult.order.statusId === 21 ? 'Booking Failed' : lastResult.order.statusId === 24 ? 'Not Shipped' : `Status ${lastResult.order.statusId}`}</div>
                {lastResult.order.lrNo && (<>
                  <div className="text-slate-500">LR No</div>
                  <div className="font-mono font-bold text-emerald-700">{lastResult.order.lrNo}</div>
                </>)}
                {lastResult.order.awbNo && (<>
                  <div className="text-slate-500">AWB No</div>
                  <div className="font-mono font-bold text-emerald-700">{lastResult.order.awbNo}</div>
                </>)}
                {lastResult.order.pickupId && (<>
                  <div className="text-slate-500">Pickup ID</div>
                  <div className="font-mono">{lastResult.order.pickupId}</div>
                </>)}
                {lastResult.order.courierBookingId && (<>
                  <div className="text-slate-500">Courier Booking ID</div>
                  <div className="font-mono">{lastResult.order.courierBookingId}</div>
                </>)}
                {lastResult.order.jobId && (<>
                  <div className="text-slate-500">Job ID</div>
                  <div className="font-mono">{lastResult.order.jobId}</div>
                </>)}
              </>
            )}
            {(lastResult.shipError || lastResult.order?.lastBookingError) && (
              <>
                <div className="text-slate-500">Error</div>
                <div className="text-rose-700 font-medium">{lastResult.shipError || lastResult.order?.lastBookingError}</div>
              </>
            )}
          </div>

          {Array.isArray(lastResult.recentApiLogs) && lastResult.recentApiLogs.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-600 mb-1">Recent Courier API Logs (this courier)</div>
              <table className="w-full text-[11px] border border-slate-200 rounded">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-600">
                    <th className="px-2 py-1">ID</th>
                    <th className="px-2 py-1">Action</th>
                    <th className="px-2 py-1">HTTP</th>
                    <th className="px-2 py-1">Error?</th>
                    <th className="px-2 py-1">Error msg</th>
                  </tr>
                </thead>
                <tbody>
                  {lastResult.recentApiLogs.map((l: any) => (
                    <tr key={l.id} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono">{l.id}</td>
                      <td className="px-2 py-1">{l.action}</td>
                      <td className="px-2 py-1">{l.statusCode}</td>
                      <td className="px-2 py-1">{l.isError ? '✗' : '✓'}</td>
                      <td className="px-2 py-1 text-rose-700">{l.err ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Link to="/courier-api-logs" className="text-xs text-emerald-700 hover:underline mt-1 inline-block">View all courier API logs →</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
